// MiniBarConsumption.js  Quick entry + list of mini-bar consumption.
import React, { useEffect, useState } from 'react';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip,
  Snackbar, Alert, CircularProgress, MenuItem, FormControlLabel, Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import HotelIcon from '@mui/icons-material/Hotel';
import { miniBarApi, lookupApi, ctx } from './modulesApi';

export default function MiniBarConsumption() {
  const { companyId, hotelId, userId, currency } = ctx();
  const [items, setItems] = useState([]); // master
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterRoom, setFilterRoom] = useState('');
  const [open, setOpen] = useState(false);
  const [guests, setGuests] = useState([]); // in-house guests
  const [selectedGuestId, setSelectedGuestId] = useState('');
  const [form, setForm] = useState({
    bookingId: null, roomId: 0, miniBarItemId: 0, quantity: 1,
    staffName: '', chargeToRoom: true, remarks: '',
  });
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const load = async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const r = await miniBarApi.listConsumptions(hotelId, { roomId: filterRoom || undefined });
      setList(r.data || []);
    } finally { setLoading(false); }
  };
  const loadMasters = async () => {
    const r = await miniBarApi.listItems(hotelId, { pageSize: 200 });
    setItems(r.data.items || []);
  };
  const loadGuests = async () => {
    try {
      const r = await lookupApi.inHouseGuests(hotelId);
      console.log('[InHouseGuests]', { hotelId, count: r.data?.length || 0, data: r.data });
      setGuests(r.data || []);
    } catch (e) {
      console.error('[InHouseGuests] failed', e);
    }
  };
  useEffect(() => { loadMasters(); load(); loadGuests(); /* eslint-disable-next-line */ }, []);

  // When user picks a guest from dropdown, auto-fill booking/room.
  const onPickGuest = (bookingId) => {
    setSelectedGuestId(bookingId);
    if (!bookingId) {
      setForm({ ...form, bookingId: null, roomId: 0 });
      return;
    }
    const g = guests.find((x) => x.bookingId === bookingId);
    if (g) {
      setForm({
        ...form,
        bookingId: g.bookingId,
        roomId: g.roomId,
        chargeToRoom: true,
      });
    }
  };

  const onRecord = async () => {
    if (!form.miniBarItemId) return notify('Pick item', 'warning');
    if (!form.roomId) return notify('Room required', 'warning');
    if (!form.quantity || form.quantity < 1) return notify('Qty', 'warning');
    const m = items.find((i) => i.miniBarItemId === form.miniBarItemId);
    if (!m) return;
    try {
      await miniBarApi.recordConsumption({
        companyId, hotelId, ...form,
        unitPrice: m.price, gstAmount: 0, lineTotal: 0, // server recalculates
      }, userId);
      notify('Consumption recorded'); setOpen(false); load(); loadMasters();
    } catch { notify('Failed', 'error'); }
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      <Card>
        <CardContent>
          <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
            <Box display='flex' alignItems='center' gap={1}>
              <LocalBarIcon color='primary' />
              <Typography variant='h5'>Mini-Bar Consumption</Typography>
            </Box>
            <Button variant='contained' startIcon={<AddIcon />} onClick={() => {
              setSelectedGuestId('');
              setForm({ bookingId: null, roomId: 0, miniBarItemId: 0, quantity: 1,
                        staffName: '', chargeToRoom: true, remarks: '' });
              loadGuests();
              setOpen(true);
            }}>Add Order</Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size='small' label='Filter by Room ID' value={filterRoom}
                onChange={(e) => setFilterRoom(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Button variant='outlined' onClick={load}>Filter</Button>
            </Grid>
          </Grid>

          <TableContainer>
            <Table size='small'>
              <TableHead><TableRow>
                <TableCell>When</TableCell><TableCell>Booking #</TableCell>
                <TableCell>Room</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Tax Type</TableCell>
                <TableCell align='right'>Qty</TableCell>
                <TableCell align='right'>Unit Price</TableCell>
                <TableCell align='right'>Tax</TableCell>
                <TableCell align='right'>Total</TableCell>
                <TableCell>Staff</TableCell><TableCell>Charge</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {loading && (<TableRow><TableCell colSpan={11} align='center'><CircularProgress size={24} /></TableCell></TableRow>)}
                {!loading && list.length === 0 && (<TableRow><TableCell colSpan={11} align='center'>No entries</TableCell></TableRow>)}
                {list.map((c) => {
                  const taxType = (c.taxType || 'GST').toUpperCase();
                  return (
                    <TableRow key={c.consumptionId} hover>
                      <TableCell>{new Date(c.consumedAt).toLocaleString('en-GB')}</TableCell>
                      <TableCell>{c.bookingId || '-'}</TableCell>
                      <TableCell>{c.roomNumber || c.roomId}</TableCell>
                      <TableCell>{c.productName}</TableCell>
                      <TableCell>
                        <Chip size='small'
                          label={`${taxType} ${Number(c.gstPercent || 0)}%`}
                          color={taxType === 'VAT' ? 'warning' : 'primary'}
                          variant='outlined' />
                      </TableCell>
                      <TableCell align='right'>{c.quantity}</TableCell>
                      <TableCell align='right'>{currency} {Number(c.unitPrice).toFixed(2)}</TableCell>
                      <TableCell align='right'>{currency} {Number(c.gstAmount).toFixed(2)}</TableCell>
                      <TableCell align='right'>{currency} {Number(c.lineTotal).toFixed(2)}</TableCell>
                      <TableCell>{c.staffName}</TableCell>
                      <TableCell>{c.chargeToRoom ? <Chip size='small' color='info' label='Room' /> : <Chip size='small' label='Direct' />}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Add Order</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth size='small' label='In-house guest (auto-fills booking + room)'
                value={selectedGuestId} onChange={(e) => onPickGuest(e.target.value)}
                InputProps={{ startAdornment: <HotelIcon fontSize='small' sx={{ mr: 1, color: 'action.active' }} /> }}>
                <MenuItem value=''>--Walk-in / manual--</MenuItem>
                {guests.map((g) => (
                  <MenuItem key={g.bookingId} value={g.bookingId}>
                    {g.roomNumber ? `Room ${g.roomNumber}  ` : ''}{g.guestName} (#{g.bookingId})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label='Item' value={form.miniBarItemId}
                onChange={(e) => setForm({ ...form, miniBarItemId: parseInt(e.target.value, 10) })}>
                <MenuItem value={0}>--Select--</MenuItem>
                {items.filter((i) => i.isActive).map((i) => (
                  <MenuItem key={i.miniBarItemId} value={i.miniBarItemId}>
                    {i.productName}  {currency}{i.price.toFixed(2)} (stock: {i.stockQuantity})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth type='number' label='Room ID' value={form.roomId}
              helperText={selectedGuestId && !form.roomId ? 'Auto-fill failed  type manually' : ''}
              onChange={(e) => setForm({ ...form, roomId: parseInt(e.target.value, 10) || 0 })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type='number' label='Booking ID (opt)' value={form.bookingId || ''}
              onChange={(e) => setForm({ ...form, bookingId: e.target.value ? parseInt(e.target.value, 10) : null })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type='number' label='Quantity' value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value, 10) || 0 })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label='Staff' value={form.staffName}
              onChange={(e) => setForm({ ...form, staffName: e.target.value })} /></Grid>
            <Grid item xs={12}><FormControlLabel control={
              <Switch checked={form.chargeToRoom} onChange={(e) => setForm({ ...form, chargeToRoom: e.target.checked })} />
            } label='Charge to room' /></Grid>
            <Grid item xs={12}><TextField fullWidth label='Remarks' value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Grid>
          </Grid>

          {/* Charge preview — uses the item's own master rate (GST or VAT),
              matching what the backend will post to the room folio + invoice. */}
          {(() => {
            const m = items.find((i) => i.miniBarItemId === form.miniBarItemId);
            if (!m) return null;
            const base = (m.price || 0) * (form.quantity || 0);
            const pct = Number(m.gstPercent || 0);
            const taxLabel = (m.taxType || 'GST').toUpperCase() === 'VAT' ? 'VAT' : 'GST';
            const tax = Math.round(base * (pct / 100) * 100) / 100;
            const total = base + tax;
            return (
              <Box display='flex' flexDirection='column' alignItems='flex-end' mt={2} gap={0.5}>
                <Typography variant='body2'>
                  Price × Qty: <strong>{currency} {base.toFixed(2)}</strong>
                </Typography>
                <Typography variant='body2'>
                  {taxLabel} ({pct}%): <strong>{currency} {tax.toFixed(2)}</strong>
                </Typography>
                <Typography variant='h6'>
                  Total: <strong>{currency} {total.toFixed(2)}</strong>
                </Typography>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={onRecord}>Add Order</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
